/**
 * API functions for Accounts BO module.
 *
 * @taskId T473
 * @see functional/Specs/specs_cycle_4.md HU-27-FE
 */

import { authFetch, normalizeApiError } from '@flexibilitypay/shared';
import type {
  Account,
  AccountDetail,
  AccountListParams,
  AccountListResponse,
  TransactionDetail,
  TransactionHistoryParams,
  TransactionHistoryResponse,
  UpdateAccountStatusPayload,
} from '../features/accounts/types/account.types';

const BASE_PATH = '/api/v1/bo/accounts';

/**
 * Error específico de la API de cuentas.
 */
export class AccountApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'AccountApiError';
  }
}

/**
 * GET /api/v1/bo/accounts — Lista cuentas con filtros y paginación.
 */
export async function getAccounts(params: AccountListParams): Promise<AccountListResponse> {
  const searchParams = new URLSearchParams();

  if (params.page !== undefined) searchParams.set('page', String(params.page));
  if (params.size !== undefined) searchParams.set('size', String(params.size));
  if (params.cvu) searchParams.set('cvu', params.cvu);
  if (params.merchantId) searchParams.set('merchantId', params.merchantId);
  if (params.estado) searchParams.set('status', params.estado);
  if (params.fechaDesde) searchParams.set('fechaDesde', params.fechaDesde);
  if (params.fechaHasta) searchParams.set('fechaHasta', params.fechaHasta);
  if (params.search) searchParams.set('search', params.search);

  const res = await authFetch(`${BASE_PATH}?${searchParams.toString()}`);
  if (!res.ok) throw await normalizeApiError(res);
  return res.json();
}

/**
 * GET /api/v1/bo/accounts/{id} — Detalle de cuenta con historial.
 */
export async function getAccountDetail(id: string): Promise<AccountDetail> {
  const res = await authFetch(`${BASE_PATH}/${id}`);
  if (!res.ok) throw await normalizeApiError(res);
  return res.json();
}

/**
 * GET /api/v1/bo/accounts/{id}/transactions — Historial de transacciones.
 */
export async function getTransactionHistory(
  params: TransactionHistoryParams,
): Promise<TransactionHistoryResponse> {
  const searchParams = new URLSearchParams();

  if (params.page !== undefined) searchParams.set('page', String(params.page));
  if (params.size !== undefined) searchParams.set('size', String(params.size));
  if (params.transactionId) searchParams.set('transactionId', params.transactionId);
  if (params.tipo) searchParams.set('tipo', params.tipo);
  if (params.estado) searchParams.set('estado', params.estado);
  if (params.fechaDesde) searchParams.set('fechaDesde', params.fechaDesde);
  if (params.fechaHasta) searchParams.set('fechaHasta', params.fechaHasta);
  if (params.montoMin) searchParams.set('montoMin', params.montoMin);
  if (params.montoMax) searchParams.set('montoMax', params.montoMax);

  const res = await authFetch(
    `${BASE_PATH}/${params.accountId}/transactions?${searchParams.toString()}`,
  );
  if (!res.ok) throw await normalizeApiError(res);
  return res.json();
}

/**
 * GET /api/v1/bo/accounts/{accountId}/transactions/{transactionId} — Detalle de transacción.
 */
export async function getTransactionDetail(
  accountId: string,
  transactionId: string,
): Promise<TransactionDetail> {
  const res = await authFetch(
    `${BASE_PATH}/${accountId}/transactions/${transactionId}`,
  );
  if (!res.ok) throw await normalizeApiError(res);
  return res.json();
}

/**
 * PATCH /api/v1/bo/accounts/{id}/status — Cambiar estado de cuenta.
 * Solo PSP_ADMIN (RULE-29).
 */
export async function updateAccountStatus(
  id: string,
  payload: UpdateAccountStatusPayload,
): Promise<Account> {
  const res = await authFetch(`${BASE_PATH}/${id}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await normalizeApiError(res);
  return res.json();
}

/**
 * GET /api/v1/bo/accounts/export — Exportar cuentas a CSV.
 */
export async function exportAccountsCsv(params: {
  estado?: string;
  merchantId?: string;
  search?: string;
}): Promise<Blob> {
  const searchParams = new URLSearchParams();
  if (params.estado) searchParams.set('status', params.estado);
  if (params.merchantId) searchParams.set('merchantId', params.merchantId);
  if (params.search) searchParams.set('search', params.search);

  const res = await authFetch(`${BASE_PATH}/export?${searchParams.toString()}`);
  if (!res.ok) throw await normalizeApiError(res);
  return res.blob();
}
